import { useQuery } from "@tanstack/react-query";
import { AnalyticsExchangeRate } from "@ternary/api-lib/analytics/api/AnalyticsApiClient";
import { CurrencyCode } from "@ternary/api-lib/constants/enums";
import UError from "unilib-error";
import registry from "unilib-registry/instance";
import { ANALYTICS_QUERY_GC_TIME } from "../../constants";
import { useAnalyticsApiClient } from "../../context/AnalyticsQueryLoaderProvider";
import { UseQueryOptions, UseQueryResult } from "../../lib/react-query";
import { queryKeys } from "./queryKeys";

interface Params {
  tenantID: string;
  baseCurrencyCode?: CurrencyCode;
  quoteCurrencyCode?: CurrencyCode;
  queryOptions: UseQueryOptions<AnalyticsExchangeRate, UError>;
  register?: boolean;
}

/**
 * Fetches the exchange rates for the given tenant and preferred currency.
 * @param tenantID - The ID of the tenant.
 * @param baseCurrencyCode - The base currency code for the tenant.
 * @param quoteCurrencyCode - The quote currency code for the tenant.
 * @param queryOptions - The options for the query.
 * @param register - Whether to register the exchange rates in the global state.
 *
 * Sets the exchange rates in the global state.
 */
export function useGetExchangeRates({
  tenantID,
  baseCurrencyCode = CurrencyCode.USD,
  quoteCurrencyCode,
  queryOptions = {},
  register,
}: Params): UseQueryResult<AnalyticsExchangeRate, UError> {
  const client = useAnalyticsApiClient();

  return useQuery({
    queryKey: queryKeys.analyticsExchangeRates(
      tenantID,
      baseCurrencyCode,
      quoteCurrencyCode
    ),
    queryFn: async () => {
      const exchangeRates = await client.getExchangeRates(
        tenantID,
        baseCurrencyCode,
        quoteCurrencyCode
      );

      if (register) {
        registry.register("exchangeRates", () => exchangeRates);
      }
      return exchangeRates;
    },
    gcTime: ANALYTICS_QUERY_GC_TIME,
    ...queryOptions,
  });
}
